import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ArrowRight } from 'lucide-react';

const IntegrationsLanding = () => {
  const navigate = useNavigate();

  const platforms = [
    {
      id: 'instagram',
      name: 'Instagram',
      description: 'Connect your Instagram Business account to automate responses to DMs and comments.',
      logo: 'https://cdn.simpleicons.org/instagram/E4405F',
      color: '#E4405F',
      status: 'Available',
      path: '/integrations/instagram'
    },
    {
      id: 'woocommerce',
      name: 'WooCommerce',
      description: 'Sync your WooCommerce product catalog and let the agent answer questions using live store data.',
      logo: 'https://cdn.simpleicons.org/woocommerce/96588A',
      color: '#96588A',
      status: 'Available',
      path: '/integrations/woocommerce'
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      description: 'Engage with your customers on WhatsApp using AI-powered automated replies.',
      logo: 'https://cdn.simpleicons.org/whatsapp/25D366',
      color: '#25D366',
      status: 'Coming Soon',
      path: null
    }
  ];

  return (
    <div className="integrations-landing p-6 sm:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 border-b border-gray-100 pb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2 tracking-tight">Integrations</h1>
          <p className="text-gray-500 font-medium">Select a platform to manage your connections and automations</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {platforms.map((platform) => (
            <div 
              key={platform.id}
              onClick={() => platform.path && navigate(platform.path)}
              className={`group relative bg-white border border-gray-200 rounded-[2rem] p-8 shadow-sm transition-all duration-300 ${
                platform.path ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1 hover:border-black' : 'opacity-70 cursor-not-allowed'
              }`}
            >
              <div className="flex items-start justify-between mb-6">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 duration-300"
                  style={{ backgroundColor: `${platform.color}10` }}
                >
                  <img src={platform.logo} alt={platform.name} className="w-10 h-10 object-contain" />
                </div>
                {platform.status === 'Coming Soon' && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-500 text-xs font-bold rounded-full">
                    {platform.status}
                  </span>
                )}
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-black transition-colors">
                {platform.name}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                {platform.description}
              </p>

              <div className="flex items-center text-sm font-bold text-gray-900 group-hover:gap-2 transition-all">
                {platform.path ? (
                  <>
                    <span>Manage Connections</span>
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-all" />
                  </>
                ) : (
                  <span className="text-gray-400">Not Available Yet</span>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default IntegrationsLanding;
